<script setup lang="ts">
const lastUpdated = "May 19, 2026";

const acceptableUseItems = [
  "Provide false, misleading, or impersonated identity, fighter, gym, or organisation information.",
  "Submit fight offers, applications, messages, or profile content that is fraudulent, abusive, discriminatory, threatening, or unlawful.",
  "Upload documents, images, or other files that you do not have the right to use.",
  "Use Fight Search to harass, spam, scrape, or target other users.",
  "Attempt to bypass verification, access controls, security restrictions, or account permissions.",
  "Interfere with the operation, reliability, or security of the service.",
];

const userResponsibilities = [
  "Keep your account credentials secure.",
  "Keep your profile, entity, fight record, and contact information accurate.",
  "Only create or manage fighter, gym, or organisation profiles that you are authorised to represent.",
  "Make your own checks before entering into any fight, event, contract, or commercial arrangement.",
  "Comply with applicable laws, sporting rules, licensing requirements, medical requirements, and event regulations.",
];

const serviceFeatures = [
  "Create and manage user accounts.",
  "Create and manage fighter, gym, and organisation profiles.",
  "Search for fighters, gyms, organisations, events, and fight opportunities.",
  "Publish, apply to, send, receive, counter, accept, or decline fight offers.",
  "Use messaging and action-center features related to fight offers and other communications.",
  "Submit verification requests and supporting documents.",
  "Receive service emails and push notifications.",
];

</script>

<template>
  <div class="terms-page">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
  
        <section class="pt-6">
          <p>
            These Terms of Service (“Terms”) govern your access to and use of
            Fight Search. <strong>Note:</strong> Fight Search is currently
            operated by two founders and is not yet operating under a separate
            legal entity (“we”, “us”, “our”).
          </p>
          <p>
            By creating an account or using Fight Search, you agree to these
            Terms. If you do not agree, do not use the service.
          </p>
          <p>You can contact us at <strong>info@fight-search.com</strong>.</p>
        </section>
  
        <section>
          <h2>1. What Fight Search Does</h2>
          <p>
            Fight Search is a platform for combat-sports participants, including
            fighters, gyms, promoters, organisations, and related users.
          </p>
          <p>The service may allow users to:</p>
          <ul>
            <li v-for="item in serviceFeatures" :key="item">{{ item }}</li>
          </ul>
        </section>
  
        <section>
          <h2>2. Accounts</h2>
          <p>
            You must provide accurate account information and keep it up to date.
            You are responsible for activity that happens through your account.
          </p>
          <p>
            You must not share your login credentials, use another person’s
            account without permission, or create accounts for misleading,
            fraudulent, abusive, or unlawful purposes.
          </p>
        </section>
  
        <section>
          <h2>3. No Current Age Limit</h2>
          <p>
            Fight Search currently does not set a specific age limit. Because the
            app may involve combat sports, fighter profiles, verification, and
            public-facing sports data, we may introduce age restrictions or
            parental consent requirements in the future.
          </p>
          <p>
            If you are responsible for a minor fighter, you must make sure that
            you have the authority to create, manage, or submit information on
            their behalf.
          </p>
        </section>
  
        <section>
          <h2>4. Profiles And Entity Representation</h2>
          <p>
            You may only create or manage fighter, gym, or organisation profiles
            that you are authorised to represent.
          </p>
          <p>
            You are responsible for the accuracy of profile information, including
            fight records, physical attributes, gym associations, organisation
            details, and uploaded media.
          </p>
          <p>
            We may remove, restrict, or correct profiles that appear inaccurate,
            misleading, unauthorised, abusive, or unlawful.
          </p>
        </section>
  
        <section>
          <h2>5. Verification</h2>
          <p>
            Fight Search may offer verification for fighters, gyms, organisations,
            or other entities. Verification is intended to increase trust, but it
            is not a guarantee of identity, eligibility, skill, medical fitness,
            event readiness, legal compliance, or contractual reliability.
          </p>
          <p>
            You must submit accurate verification information and documents. We
            may approve, reject, expire, request more information, or revoke
            verification at our discretion if information is incomplete,
            inaccurate, outdated, unauthorised, or suspicious.
          </p>
        </section>
  
        <section>
          <h2>6. Fight Offers, Applications, And Messaging</h2>
          <p>
            Fight Search may let users publish, send, receive, apply to, counter,
            accept, or decline fight offers. These features are tools for
            communication and coordination.
          </p>
          <p>
            Unless we explicitly state otherwise in writing, Fight Search is not a
            party to fight offers, contracts, event agreements, bout agreements,
            payment arrangements, sponsorships, or other deals made between users.
          </p>
          <p>
            Users are responsible for checking all relevant details before relying
            on a fight offer or application, including opponent identity, rules,
            weight class, purse, travel arrangements, medical requirements,
            licensing, insurance, event sanctioning, and legal obligations.
          </p>
        </section>
  
        <section>
          <h2>7. Guest Invites</h2>
          <p>
            Fight Search may allow users to send private fight offers or invites
            to people who do not yet have an account. You must only send guest
            invites where you have a legitimate reason to contact the recipient.
          </p>
          <p>
            You must not use guest invites for spam, harassment, unsolicited mass
            messaging, misleading offers, or unlawful marketing.
          </p>
        </section>
  
        <section>
          <h2>8. User Responsibilities</h2>
          <p>When using Fight Search, you agree to:</p>
          <ul>
            <li v-for="item in userResponsibilities" :key="item">{{ item }}</li>
          </ul>
        </section>
  
        <section>
          <h2>9. Acceptable Use</h2>
          <p>You must not:</p>
          <ul>
            <li v-for="item in acceptableUseItems" :key="item">{{ item }}</li>
          </ul>
        </section>
  
        <section>
          <h2>10. User Content</h2>
          <p>
            You retain ownership of content you submit to Fight Search, such as
            profile information, messages, fight offers, uploaded images,
            documents, and other materials.
          </p>
          <p>
            You grant us a non-exclusive, worldwide, royalty-free licence to host,
            store, display, process, transmit, and use that content as needed to
            operate, secure, improve, and provide Fight Search.
          </p>
          <p>
            You are responsible for making sure that you have the necessary rights
            to submit and use your content on Fight Search.
          </p>
        </section>
  
        <section>
          <h2>11. Public Profile Information</h2>
          <p>
            Fighter, gym, and organisation profiles are not currently visible to
            non-logged-in visitors. In the future, some profile information may
            become publicly visible.
          </p>
          <p>
            Before public profile access is enabled, Fight Search should clearly
            indicate which profile information will be public.
          </p>
        </section>
  
        <section>
          <h2>12. Payments And Subscriptions</h2>
          <p>
            Payments and subscriptions are not currently active, but may be added
            using Stripe.
          </p>
          <p>
            When paid features become available, additional payment terms may
            apply. Stripe may process payment information. We will not store full
            card details ourselves.
          </p>
          <p>
            Any fees, renewal terms, cancellation rights, taxes, refunds, and paid
            feature limitations should be shown before you purchase a paid
            service.
          </p>
        </section>
  
        <section>
          <h2>13. Notifications</h2>
          <p>
            We may send service emails and push notifications related to account
            activity, verification, fight offers, applications, messages, and
            important service updates.
          </p>
          <p>
            You can disable push notifications through your device or browser
            settings. Some service emails may still be necessary for account,
            security, verification, or transactional purposes.
          </p>
        </section>
  
        <section>
          <h2>14. Privacy</h2>
          <p>
            Our Privacy Policy explains how we collect, use, store, and protect
            personal data. By using Fight Search, you acknowledge that your data
            is processed as described in the Privacy Policy.
          </p>
          <p>The Privacy Policy is available in the app.</p>
        </section>
  
        <section>
          <h2>15. Service Availability And Changes</h2>
          <p>
            We may modify, suspend, restrict, or discontinue parts of Fight Search
            at any time. We aim to keep the service reliable, but we do not
            guarantee uninterrupted availability or error-free operation.
          </p>
          <p>
            We may update features, eligibility rules, verification requirements,
            public visibility settings, or paid functionality as the product
            develops.
          </p>
        </section>
  
        <section>
          <h2>16. Suspension And Termination</h2>
          <p>
            We may suspend, restrict, or terminate your access if we believe that
            you have violated these Terms, created risk for other users, provided
            false information, abused the service, or used Fight Search
            unlawfully.
          </p>
          <p>
            You may stop using Fight Search at any time. You may request account
            deletion by contacting us.
          </p>
        </section>
  
        <section>
          <h2>17. Disclaimers</h2>
          <p>
            Fight Search is provided “as is” and “as available”. To the maximum
            extent permitted by law, we do not make warranties about the accuracy,
            completeness, reliability, availability, suitability, safety, or
            legality of user content, profiles, fight offers, events, or
            communications.
          </p>
          <p>
            We do not guarantee that any fighter, gym, organisation, promoter,
            event, offer, application, or user is suitable, safe, eligible,
            licensed, insured, medically cleared, or legally compliant.
          </p>
        </section>
  
        <section>
          <h2>18. Limitation Of Liability</h2>
          <p>
            To the maximum extent permitted by law, we are not liable for
            indirect, incidental, special, consequential, punitive, or
            loss-of-profit damages arising from your use of Fight Search.
          </p>
          <p>
            Nothing in these Terms excludes liability that cannot be excluded
            under applicable law.
          </p>
        </section>
  
        <section>
          <h2>19. Changes To These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make significant
            changes, we will notify users through the app or by email where
            appropriate.
          </p>
          <p>
            Continued use of Fight Search after updated Terms become effective
            means you accept the updated Terms.
          </p>
        </section>
  
        <section>
          <h2>20. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the
            <strong>Republic of Slovenia</strong>, unless mandatory consumer
            protection laws require otherwise.
          </p>
        </section>
  
        <section>
          <h2>21. Contact</h2>
          <p>
            Contact email:
            <strong>info@fight-search.com</strong>
          </p>
          <p>
            Fight Search is currently operated by two founders and is not yet
            operating under a separate legal entity.
          </p>
        </section>
      </div>
</template>

<style scoped>
.terms-page > section {
  margin-top: 3rem;
}

.terms-page > section:first-of-type {
  margin-top: 0;
}

.terms-page h2 {
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.terms-page p,
.terms-page li {
  line-height: 1.7;
}
</style>
